import React, {useContext} from "react";
import PropTypes from "prop-types";
import {makeStyles, styled} from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import {CardActions, CardHeader} from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {LanguageContext} from "../../context/LanguageContext";
import Grid from "@mui/material/Grid";
import Rendered from "../Rendered";
import {LoadingButton} from "@mui/lab";

const useStyles = makeStyles(theme => ({
  appBar: {
    position: "relative"
  },
  appBarSmall: {
    position: "relative",
    paddingRight: "0 !important",
    minHeight: 48
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1
  },
  titleSmall: {
    marginLeft: theme.spacing(1),
    flex: 1
  }
}));

const ButtonStyled = styled(LoadingButton)(() => ({
  textTransform: 'none'
}));

const SimpleDialog = ({open, title, handleClose, fullScreen, maxWidth, newDesign, buttonProps = {}, ...props}) => {
  const classes = useStyles();
  const languageContext = useContext(LanguageContext);

  const handleDialogClose = (event, reason) => {
    // “disableBackdropClick of ForwardRef(Dialog) is deprecated”
    if (props.blocking && reason === "backdropClick") {
      return false;
    }

    if (props.blocking && reason === "escapeKeyDown") {
      return false;
    }

    if (typeof handleClose === "function") {
      handleClose();
    }
  };

  const hasProp = prop => !!prop && Object.keys(prop)?.length > 0;

  if (newDesign) {
    return (
      <Dialog
        open={open}
        onClose={handleDialogClose}
        fullScreen={fullScreen}
        maxWidth={maxWidth || false}
        disableEnforceFocus={true}
      >
        <Card sx={{padding: 1}}>
          <CardHeader
            action={
              <IconButton color="secondary" aria-label="close" onClick={handleClose}>
                <CloseIcon fontSize="small"/>
              </IconButton>
            }
            title={<Typography variant="h5" sx={{fontWeight: "bold"}}>{title}</Typography>}
            sx={{paddingBottom: 0}}
          />
          <CardContent sx={{maxHeight: "80vw", overflow: "auto"}}>
            {props.children}
          </CardContent>
          <CardActions>
            <Rendered rendered={hasProp(buttonProps)}>
              <Grid container sx={{justifyContent: "space-between"}}>
                <Grid item xs={4}>
                  <Rendered rendered={hasProp(buttonProps.close)}>
                    <ButtonStyled color="secondary" variant="text" onClick={handleClose} loading={props.loading}>
                      {buttonProps.close?.title || languageContext.dictionary.close}
                    </ButtonStyled>
                  </Rendered>
                </Grid>
                <Grid item xs={hasProp(buttonProps.close) ? 8 : 12} sx={{textAlign: "right"}}>
                  <Rendered rendered={hasProp(buttonProps.cancel)}>
                    <ButtonStyled
                      color={buttonProps.cancel?.color || "secondary"}
                      variant="outlined"
                      sx={{marginRight: 2}}
                      onClick={buttonProps.cancel?.onClick || handleClose}
                      loading={props.loading}
                    >
                      {buttonProps.cancel?.title || languageContext.dictionary.cancel}
                    </ButtonStyled>
                  </Rendered>
                  <Rendered rendered={hasProp(buttonProps.ok)}>
                    <ButtonStyled color="secondary" variant="contained" onClick={buttonProps.ok?.onClick} loading={props.loading}>
                      {buttonProps.ok?.title || "OK"}
                    </ButtonStyled>
                  </Rendered>
                </Grid>
              </Grid>
            </Rendered>
          </CardActions>
        </Card>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullScreen={fullScreen}
      maxWidth={false}
      disableEnforceFocus={true}
    >
      <AppBar className={fullScreen ? classes.appBar : classes.appBarSmall} elevation={fullScreen ? 4 : 0}>
        <Toolbar style={{minHeight: fullScreen ? 64 : 48}}>
          <Typography
            className={fullScreen ? classes.title : classes.titleSmall}
            variant="h6"
          >
            {title}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            aria-label="close"
            onClick={handleClose}
            size="large"
          >
            <CloseIcon/>
          </IconButton>
        </Toolbar>
      </AppBar>
      {props.children}
    </Dialog>
  );
};

SimpleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  fullScreen: PropTypes.bool
};

export default SimpleDialog;
